import { createFileRoute } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { canonical } from "@/lib/seo";

const TITLE = "Privacy Policy | Durafoam Roofing";
const DESCRIPTION = "How Durafoam Roofing collects, uses, and protects your personal information.";

export const Route = createFileRoute("/privacy-policy")({
  head: () => {
    const url = canonical("/privacy-policy");
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { name: "robots", content: "noindex" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <Container className="prose prose-slate max-w-3xl py-12 lg:py-16">
      <h1 className="text-3xl font-bold text-primary">Privacy Policy</h1>
      <p className="text-sm text-[var(--muted-foreground)]">Last updated: June 2026</p>

      <p>
        Durafoam Roofing LLC ("we", "us", "our") respects your privacy. This policy explains what
        we collect when you use durafoaminc.com, how we use it, and how to contact us about your
        data.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">What we collect</h2>
      <p>When you submit a contact form, inspection request, or blog signup, we collect:</p>
      <ul className="list-disc pl-6">
        <li>Your name</li>
        <li>Your phone number</li>
        <li>Your email address (when you provide one)</li>
        <li>Any message or service request details you include</li>
        <li>The page on our website the request came from</li>
      </ul>
      <p>
        We do not collect payment information through this website.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">How we use your information</h2>
      <ul className="list-disc pl-6">
        <li>To contact you about the roofing services you requested</li>
        <li>To schedule and follow up on free roof inspections</li>
        <li>To answer questions and provide estimates</li>
      </ul>
      <p>
        We <strong>do not</strong> sell, rent, or share your personal information with third
        parties for marketing purposes.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Cookies and analytics</h2>
      <p>
        We may use Google Analytics to understand which pages are useful to visitors. Analytics
        cookies do not personally identify you. You can disable cookies in your browser settings.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Where your data is stored</h2>
      <p>
        Contact form submissions are stored securely in our backend database (Supabase) and are
        only accessible to authorized Durafoam staff.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Request deletion of your data</h2>
      <p>
        You can request that we delete any personal information you've submitted through this
        site. Email{" "}
        <a href="mailto:quality@durafoaminc.com" className="text-accent hover:underline">
          quality@durafoaminc.com
        </a>{" "}
        with the phone number or email you submitted, and we'll remove your record within 30 days.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Changes to this policy</h2>
      <p>
        We may update this policy from time to time. The "last updated" date above will reflect
        the most recent change.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-primary">Contact</h2>
      <p>
        Questions? Email{" "}
        <a href="mailto:quality@durafoaminc.com" className="text-accent hover:underline">
          quality@durafoaminc.com
        </a>{" "}
        or call 480-941-5373.
      </p>
    </Container>
  );
}
